<template>
  <v-card v-if="(loading || error || (widgetData && widgetData.status)) && !iframeError" class="widget-tool-wrapper rounded-lg" outlined>

    <div v-if="loading" class="d-flex flex-column justify-center align-center py-10">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>

    <div v-else-if="error" class="error-state d-flex flex-column align-center text-center py-8 px-4">
      <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
      <p class="mt-3 mb-4 font-weight-medium grey--text text--darken-2">{{ error }}</p>
      <v-btn color="primary" outlined small class="text-capitalize rounded-lg font-weight-bold" @click="fetchWidgetData">
        <v-icon left small>mdi-refresh</v-icon> {{ $t('generic.retry') || 'Riprova' }}
      </v-btn>
    </div>

    <div v-else-if="widgetData && widgetData.status" class="widget-content d-flex justify-center align-center w-100">

      <div class="responsive-iframe-container">
        <div v-if="iframeLoading" class="d-flex align-center justify-center fill-height loading-overlay">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <iframe
            v-if="widgetUrl"
            v-show="!iframeLoading"
            :src="widgetUrl"
            @load="onIframeLoad"
            @error="onIframeError"
            class="responsive-iframe"
            style="border: none;"
            allowfullscreen
        >
        </iframe>
      </div>

    </div>

  </v-card>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'StreamingTheSports',

  props: {
    sportId: {
      type: [String, Number],
      required: true
    },
    eventId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      widgetData: null,
      widgetUrl: null,
      iframeLoading: true,
      iframeError: false,
      iframeTimeout: null,
    };
  },

  computed: {
    ...mapGetters({
      getToken: 'settings/getToken',
      getUrl: 'settings/getUrlApi',
      getPlatformId: 'settings/getPlatformId',
      getLanguage: 'settings/getLanguage',
    })
  },

  watch: {
    sportId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchWidgetData();
      }
    },
    eventId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchWidgetData();
      }
    },
    widgetUrl: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.checkWidgetAvailability(newUrl);
        } else {
          this.iframeLoading = false;
        }
      }
    },
    iframeError(val) {
      if (val) this.$emit('unavailable');
    }
  },

  mounted() {
    this.fetchWidgetData();
    window.addEventListener('message', this.handleIframeMessage);
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleIframeMessage);
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }
  },

  methods: {
    async fetchWidgetData() {
      if (!this.sportId || !this.eventId) return;

      this.loading = true;
      this.error = null;
      this.widgetData = null;
      this.widgetUrl = null;
      this.iframeError = false;
      this.iframeLoading = true;

      try {
        const response = await axios.get(`${this.getUrl}/v3/widgets/widget-tool`, {
          params: {
            platformId: this.getPlatformId,
            sportId: this.sportId
          },
          headers: {
            Authorization: `Bearer ${this.getToken}`
          }
        });

        this.widgetData = response.data?.data || response.data;

        if (this.widgetData && this.widgetData.streamEnabled) {
          if (this.widgetData.streamToken && this.widgetData.streamUrl) {

            const uuidResponse = await axios.get(`${this.getUrl}/v3/the-sports/events/by-id`, {
              params: {
                platformId: this.getPlatformId,
                sport: this.widgetData.sportNameFormatted || 'football',
                event: this.eventId
              },
              headers: {
                Authorization: `Bearer ${this.getToken}`
              }
            });

            const matchUuid = uuidResponse.data?.data;

            if (matchUuid) {
              this.widgetUrl = this.widgetData.streamUrl
                  .replace(/{\s*language\s*}/g, this.getLanguage || 'en')
                  .replace(/{\s*match_(uuid|id)\s*}/g, matchUuid);
            } else {
              this.iframeError = true;
            }

          } else {
            this.iframeError = true;
          }
        } else {
          this.iframeError = true;
        }

      } catch (err) {
        console.error('[StreamingTheSports] Errore API:', err);
        this.error = this.$t('generic.errorLoad') || 'Errore nel caricamento dello stream.';
      } finally {
        this.loading = false;
      }
    },

    async checkWidgetAvailability(url) {
      this.iframeLoading = true;
      this.iframeError = false;

      if (this.iframeTimeout) clearTimeout(this.iframeTimeout);

      this.iframeTimeout = setTimeout(() => {
        if (this.iframeLoading) {
          this.iframeError = true;
          this.iframeLoading = false;
        }
      }, 15000);

      try {
        let available = await fetch(url, { mode: 'no-cors' });
        if (!available.ok && available.type !== 'opaque') {
          this.iframeError = true;
          this.iframeLoading = false;
          if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
        }
      } catch (error) {
        console.error('Errore di rete raggiungendo lo stream:', error);
        this.iframeError = true;
        this.iframeLoading = false;
        if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
      }
    },

    handleIframeMessage(event) {
      if (event.data) {
        let payload = event.data;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (e) {}
        }

        if (payload && (payload.error || payload.status === 'error' || payload.event === 'error' || payload.message === 'Domain not authorized')) {
          this.iframeError = true;
          this.iframeLoading = false;
          if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
        }
      }
    },

    onIframeLoad() {
      this.iframeLoading = false;
      if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
    },

    onIframeError() {
      this.iframeError = true;
      this.iframeLoading = false;
      if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
    }
  }
};
</script>

<style scoped>
.widget-tool-wrapper {
  width: 100%;
  background-color: var(--bg-panel, #ffffff);
  border-color: var(--border-color, #e0e0e0) !important;
  overflow: hidden;
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.bg-light-grey {
  background-color: rgba(0, 0, 0, 0.03);
}

.widget-content {
  width: 100%;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: var(--bg-panel, #ffffff);
  z-index: 2;
}

/* =========================================
   SOLUZIONE PER STREAMING VIDEO
   ========================================= */

.responsive-iframe-container {
  position: relative;
  width: 100%;

  /* SCELTA DELL'ASPECT RATIO:
    1. Se l'iframe è PURO VIDEO (standard televisivo), usa 16 / 9.
    2. Se l'iframe include il video in alto e tab/statistiche in basso
       (come suggeriscono le dimensioni 320x309), usa 320 / 309.
  */
  aspect-ratio: 16 / 9; /* Cambialo in 320 / 309 se vedi elementi tagliati in basso */
}

.responsive-iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* Breakpoint Mobile */
@media (max-width: 600px) {
  .widget-tool-wrapper {
    margin-top: 0px;
    border-radius: 0 !important;
    border-left: none !important;
    border-right: none !important;
  }
}
</style>